import { useEffect, useState } from 'react';
import {
  DropDown,
  FlexCenter,
  FlexColumn,
  FormControls,
  Radio,
} from '@elementary/common-ui/components';
import { AnomalySensitivity } from '@elementary/common-ui/types';
import { FormControlLabel, RadioGroup, Typography } from '@mui/material';
import { Controller, UseFormReturn } from 'react-hook-form';
import { AddTestForm } from 'shared/types';
import TimeWithUnitField from 'shared/components/TimeWithUnitField';
import { sensitivityOptions } from 'shared/constants';
import {
  DEFAULT_ANOMALY_TRAINING_PERIOD,
  thresholdUnitOptions,
} from '../constants';
import SeveritySelector from '../SeveritySelector';
import TestMetadataForm from './shared/TestMetadataForm';
import WizardTrainingPeriodSection from './shared/WizardTrainingPeriodSection';

type Props = {
  methods: UseFormReturn<AddTestForm, any, undefined>;
};

const TablesFreshnessAnomaliesCloud = ({ methods }: Props) => {
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    methods.setValue(
      'anomalyConfig.sensitivity',
      sensitivityOptions[1].value as AnomalySensitivity,
    );
    methods.setValue(
      'anomalyConfig.trainingPeriod',
      DEFAULT_ANOMALY_TRAINING_PERIOD,
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FormControls.TestAccordion
      summary="Freshness anomalies"
      expanded={expanded}
      onChange={(_, val) => setExpanded(val)}
    >
      <FlexColumn sx={{ gap: 3, p: 3 }}>
        <FormControls.FieldsWrapper>
          <RadioGroup
            sx={{ gap: 3 }}
            value={
              methods.watch('anomalyConfig.fixedThreshold') == null
                ? 'auto'
                : 'manual'
            }
          >
            <FormControls.Row>
              <FormControlLabel
                sx={{ mr: 0 }}
                value="auto"
                control={<Radio size="small" />}
                onChange={() =>
                  methods.setValue('anomalyConfig.fixedThreshold', null)
                }
                label={
                  <FlexCenter>
                    <Typography component="span" sx={{ mr: 1 }}>
                      Anomaly detection - automatically detect the SLA with a
                      sensitivity level of
                    </Typography>
                    <Controller
                      control={methods.control}
                      name="anomalyConfig.sensitivity"
                      disabled={
                        methods.watch('anomalyConfig.fixedThreshold') != null
                      }
                      render={({ field }) => (
                        <DropDown
                          sx={{ minWidth: '100px' }}
                          sortOptions={false}
                          placeholder="Select sensitivity"
                          options={sensitivityOptions}
                          {...field}
                        />
                      )}
                    />
                  </FlexCenter>
                }
              />
            </FormControls.Row>

            <FormControls.Row>
              <FormControlLabel
                sx={{ mr: 0 }}
                value="manual"
                control={<Radio size="small" />}
                onChange={() =>
                  methods.setValue('anomalyConfig.fixedThreshold', {
                    count: 1,
                    period: 'day',
                  })
                }
                label={
                  <>
                    <Typography component="span" sx={{ mr: 1 }}>
                      {`Static SLA - alert when table hasn't updated for`}
                    </Typography>
                    <Controller
                      control={methods.control}
                      name="anomalyConfig.fixedThreshold"
                      render={({ field }) => (
                        <TimeWithUnitField
                          disabled={
                            methods.watch('anomalyConfig.fixedThreshold') ==
                            null
                          }
                          onChange={field.onChange}
                          count={methods.watch(
                            'anomalyConfig.fixedThreshold.count',
                          )}
                          period={methods.watch(
                            'anomalyConfig.fixedThreshold.period',
                          )}
                          options={thresholdUnitOptions}
                          defaultPeriod="day"
                        />
                      )}
                    />
                  </>
                }
              />
            </FormControls.Row>
          </RadioGroup>
        </FormControls.FieldsWrapper>

        <WizardTrainingPeriodSection methods={methods} />

        <FormControls.FieldsWrapper>
          <Controller
            name="severity"
            control={methods.control}
            defaultValue={{ type: 'warn' }}
            render={({ field }) => <SeveritySelector {...field} />}
          />
        </FormControls.FieldsWrapper>

        <TestMetadataForm methods={methods} />
      </FlexColumn>
    </FormControls.TestAccordion>
  );
};

export default TablesFreshnessAnomaliesCloud;
